import React from 'react';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import Select from '../../components/ui/Select/Select';
import TimePicker from '../../components/ui/TimePicker/TimePicker';
import { AlertTriangleIcon } from '../../components/ui/Icons';
import {
  DeliverySchedule,
  saveNotificationSchedule,
  saveUserNotificationSchedule,
} from '../../notifications/api';
import { DEFAULT_TIME_ZONE, timeZoneOptions } from '../../lib/timeZones';
import base from './NotificationSettings.module.css';
import styles from './DeliveryHours.module.css';

/**
 * A QUÉ HORAS LE LLEGAN SUS AVISOS A UNA PERSONA (09-oct-2026).
 *
 * > *"La configuración de notificaciones por horario las dejaste por carga cuando debería ser por usuario, el usuario
 * > puede configurar esos horarios según le convenga de manera general, no de carga por carga"*
 *
 * ## 🔑 Dos formas, y la de siempre es la primera
 *
 * - **As they happen**: cada aviso llega cuando se crea. Es lo que había hasta hoy, y lo que tiene todo el mundo
 *   mientras no elija otra cosa.
 * - **At set times**: lo que pase en medio espera y llega **junto** a la siguiente hora. En la campanita, un renglón
 *   por aviso (cada uno lleva a su embarque); por correo, un solo correo con todo.
 *
 * ## ⚠️ Lo que la sección tiene que dejar claro
 *
 * Si las horas **no están reteniendo nada** en este servidor. Pasa en dos casos que el backend distingue: falta el SQL
 * de la tabla (`available: false`, no se puede guardar) o el reloj que entrega lo retenido está apagado
 * (`enforced: false`, se guarda pero todo sigue llegando cuando pasa). Sin decirlo, alguien elegiría las 08:00, le
 * seguirían llegando avisos a las 11:00 y concluiría que la pantalla no sirve.
 *
 * 📌 Tiene su propio botón de guardar, aparte de la tabla de avisos: son dos decisiones distintas —qué me llega y
 * cuándo—, y un solo botón obligaría a revisar las dos para cambiar una.
 */

interface DeliveryHoursProps {
  initial: DeliverySchedule;
  /** Id del usuario a editar. Sin él, edita al de la sesión. Mismo contrato que `NotificationSettings`. */
  userId?: string;
  userLabel?: string;
}

/** El mismo tope del backend y de los contactos de cliente: más de cuatro y dejan de leerse. */
const MAX_TIMES = 4;

/** `['18:00', '08:00', '08:00']` → `'08:00,18:00'`. `null` si no queda ninguna hora válida. */
const normalize = (times: string[]): string | null => {
  const valid = Array.from(
    new Set(times.map((t) => (t || '').slice(0, 5)).filter((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t)))
  ).sort();
  return valid.length ? valid.slice(0, MAX_TIMES).join(',') : null;
};

const splitHours = (sendHours: string | null): string[] =>
  sendHours ? sendHours.split(',').filter(Boolean) : ['08:00'];

const DeliveryHours: React.FC<DeliveryHoursProps> = ({ initial, userId, userLabel }) => {
  const ajeno = Boolean(userId);

  const [saved, setSaved] = React.useState<DeliverySchedule>(initial);
  const [mode, setMode] = React.useState<'live' | 'hours'>(initial.sendHours ? 'hours' : 'live');
  const [times, setTimes] = React.useState<string[]>(splitHours(initial.sendHours));
  const [zone, setZone] = React.useState(initial.timeZone || DEFAULT_TIME_ZONE);
  const [saving, setSaving] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{ ok: boolean; text: string } | null>(null);

  const groupName = React.useId();

  const proposal = mode === 'live' ? null : normalize(times);
  const invalid = mode === 'hours' && !proposal;
  /**
   * 📌 Se compara lo **normalizado** contra lo guardado, no lo que hay en los selectores: elegir `18:00` y luego
   * `08:00` deja el mismo horario que `08:00, 18:00`, y el botón no debería decir que hay algo que guardar.
   */
  const dirty =
    proposal !== (saved.sendHours || null) || (mode === 'hours' && zone !== (saved.timeZone || DEFAULT_TIME_ZONE));

  const setTime = (index: number, value: string | null) => {
    setFeedback(null);
    setTimes((list) => list.map((t, i) => (i === index ? value || '' : t)));
  };

  const removeTime = (index: number) => {
    setFeedback(null);
    setTimes((list) => (list.length > 1 ? list.filter((_, i) => i !== index) : list));
  };

  const addTime = () => {
    setFeedback(null);
    setTimes((list) => (list.length < MAX_TIMES ? [...list, '18:00'] : list));
  };

  const save = async () => {
    if (invalid || !dirty) return;
    setSaving(true);
    setFeedback(null);
    try {
      const next = userId
        ? await saveUserNotificationSchedule(userId, proposal, zone)
        : await saveNotificationSchedule(proposal, zone);
      setSaved(next);
      setTimes(splitHours(next.sendHours));
      setMode(next.sendHours ? 'hours' : 'live');
      setFeedback({
        ok: true,
        text: next.sendHours
          ? `Saved${ajeno ? ` for ${userLabel || 'that user'}` : ''}: notifications arrive at ${next.sendHours.replace(/,/g, ', ')}.`
          : `Saved${ajeno ? ` for ${userLabel || 'that user'}` : ''}: notifications arrive as they happen.`,
      });
    } catch (err: any) {
      setFeedback({ ok: false, text: err?.response?.data?.message || 'Could not save the delivery hours' });
    } finally {
      setSaving(false);
    }
  };

  const title = ajeno ? 'Delivery hours for this user' : 'Delivery hours';

  /**
   * ⚠️ Sin la tabla no se ofrece nada que no se pueda guardar: se dice qué pasa hoy y por qué. Es un faltante del
   * despliegue, no algo que la persona pueda resolver.
   */
  if (!initial.available) {
    return (
      <Card>
        <h2 className={base.cardTitle}>{title}</h2>
        <div className={[base.notice, base.noticeWarn].join(' ')}>
          <AlertTriangleIcon size={17} />
          <span>
            Choosing set times isn&apos;t available on this server yet. Until then, every notification arrives as it
            happens.
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card className={saving ? 'dylo-loading-host' : undefined}>
      {saving && <span className="dylo-loading-sweep" />}
      <h2 className={base.cardTitle}>{title}</h2>
      <p className={base.cardHint}>
        {ajeno ? (
          <>
            When <strong>{userLabel || 'this user'}</strong> gets their notifications. You are changing someone
            else&apos;s settings, and the change is recorded with your name.
          </>
        ) : (
          <>
            Choose when your notifications reach you. This applies to every shipment you get notified about, in the
            bell and by email.
          </>
        )}
      </p>

      {/**
       * 🔴 El reloj apagado se dice **arriba** y en tono de aviso: con él apagado, las horas se guardan pero no retienen
       * nada, y eso es exactamente lo que alguien no sospecharía.
       */}
      {!saved.enforced && (
        <div className={[base.notice, base.noticeWarn].join(' ')}>
          <AlertTriangleIcon size={17} />
          <span>
            This server isn&apos;t running the delivery clock yet, so notifications still arrive as they happen. Saved
            hours start working as soon as it&apos;s on.
          </span>
        </div>
      )}

      <fieldset className={styles.modes}>
        <legend className={styles.legend}>How notifications arrive</legend>

        <label className={[styles.mode, mode === 'live' ? styles.modeOn : ''].filter(Boolean).join(' ')}>
          <input
            type="radio"
            name={groupName}
            className={styles.modeInput}
            checked={mode === 'live'}
            onChange={() => {
              setFeedback(null);
              setMode('live');
            }}
          />
          <span className={styles.modeText}>
            <span className={styles.modeLabel}>As they happen</span>
            <span className={styles.modeDesc}>Each notification arrives the moment it is created.</span>
          </span>
        </label>

        <label className={[styles.mode, mode === 'hours' ? styles.modeOn : ''].filter(Boolean).join(' ')}>
          <input
            type="radio"
            name={groupName}
            className={styles.modeInput}
            checked={mode === 'hours'}
            onChange={() => {
              setFeedback(null);
              setMode('hours');
            }}
          />
          <span className={styles.modeText}>
            <span className={styles.modeLabel}>At set times</span>
            <span className={styles.modeDesc}>
              Everything in between waits and arrives together: one line per update in the bell, and a single email.
            </span>
          </span>
        </label>
      </fieldset>

      {mode === 'hours' && (
        <div className={styles.hoursBlock}>
          <span className={styles.fieldLabel}>At</span>
          <div className={styles.times}>
            {times.map((t, i) => (
              <span key={i} className={styles.time}>
                <TimePicker value={t || null} onChange={(v) => setTime(i, v)} allowClear={false} />
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={() => removeTime(i)}
                  disabled={times.length <= 1}
                  aria-label={`Remove ${t || 'this time'}`}
                >
                  ×
                </button>
              </span>
            ))}
            <button
              type="button"
              className={styles.addBtn}
              onClick={addTime}
              disabled={times.length >= MAX_TIMES}
            >
              + Add a time
            </button>
          </div>
          {/**
           * 📌 Se dice el tope **y** su razón, igual que en los envíos programados: cuatro no es un límite técnico.
           */}
          <p className={styles.hint}>
            24-hour times, up to four. More than that and the summaries stop being read.
          </p>

          <span className={styles.fieldLabel}>Time zone</span>
          <div className={styles.zone}>
            <Select
              value={zone}
              onChange={(v) => {
                setFeedback(null);
                setZone(String(v ?? DEFAULT_TIME_ZONE));
              }}
              options={timeZoneOptions(zone)}
            />
          </div>
        </div>
      )}

      {feedback && (
        <div
          className={[base.notice, feedback.ok ? base.noticeOk : base.noticeError].join(' ')}
          role="status"
        >
          <AlertTriangleIcon size={17} />
          <span>{feedback.text}</span>
        </div>
      )}

      <div className={base.actions}>
        <span className={base.dirtyHint}>
          {invalid ? 'Pick at least one time' : dirty ? 'Not saved yet' : 'Everything saved'}
        </span>
        <Button type="button" disabled={!dirty || invalid} loading={saving} onClick={save}>
          Save delivery hours
        </Button>
      </div>
    </Card>
  );
};

export default DeliveryHours;
